/**
 * Custom fetch logger that replaces Next.js built-in fetch logging.
 *
 * Called from instrumentation.ts to monkey-patch globalThis.fetch.
 * Uses Winston namespaced logger for consistent formatting.
 *
 * Enable via ENABLE_FETCH_LOGGING=true environment variable.
 * Filter URLs via LOG_FETCH_URLS=pattern1,-excludePattern
 *
 * @example Output in dev:
 * 19:20:51                    maestro:fetch: GET sanity.io /v2024/data/query/staging 200 in 45ms (no-store)
 * 19:20:52                    maestro:fetch: POST hybris.com /occ/v2/products 201 in 123ms
 *
 * @example Output with ENABLE_JSON_LOGGING=true:
 * {"timestamp":"2024-01-01T12:00:00.000Z","level":"http","namespace":"maestro:fetch","method":"GET","host":"sanity.io","path":"/v2024/data/query/staging","status":200,"duration":45,"cache":"no-store"}
 */

type LoggerType = Record<string, (...args: unknown[]) => void>
// Cache loggers by namespace to avoid creating new ones for each fetch
const loggerCache = new Map<string, LoggerType>()

/**
 * Check if JSON logging is enabled.
 * Defaults to true in production, false otherwise.
 */
function isJsonLoggingEnabled(): boolean {
  const env = process.env.ENABLE_JSON_LOGGING
  if (env === 'true' || env === '1') {
    return true
  }
  if (env === 'false' || env === '0') {
    return false
  }
  // Default: enabled in production
  return process.env.NODE_ENV === 'production'
}

/**
 * Format and output a JSON log entry for fetch requests.
 * Writes directly to stdout to bypass console interception.
 *
 * Uses structure:
 * - Fixed fields: level, namespace, message, ts
 * - error: Error object (for failed requests)
 * - meta: Request details (method, host, path, url, status, duration, cache)
 */
function logFetchJson(entry: {
  level: 'http' | 'error'
  method: string
  host: string
  path: string
  url: string
  status?: number
  duration: number
  cache?: string
  error?: string
}): void {
  const output: Record<string, unknown> = {
    level: entry.level,
    namespace: 'maestro:fetch',
    message: `${entry.method} ${entry.host} ${entry.path}`,
    ts: new Date().toISOString(),
  }

  // Add error object for failed requests
  if (entry.error) {
    output.error = { name: 'FetchError', message: entry.error }
  }

  // All request details go to meta
  output.meta = {
    method: entry.method,
    host: entry.host,
    path: entry.path,
    url: entry.url,
    ...(entry.status !== undefined && { status: entry.status }),
    duration: entry.duration,
    ...(entry.cache && { cache: entry.cache }),
  }

  // Write directly to stdout to bypass console interception
  process.stdout.write(JSON.stringify(sanitizeLogs([output])[0]) + '\n')
}

import { ANSI_COLORS } from '@maestro/core/logger/constants'
import {
  sanitizeLogs,
  sanitizeString,
} from '@maestro/core/logger/log-sanitizer'

// Alias for backwards compatibility within this file
const colors = ANSI_COLORS

/**
 * Color status code based on HTTP status
 */
function colorStatus(status: number): string {
  if (status >= 500) {
    return `${colors.red}${status}${colors.reset}`
  }
  if (status >= 400) {
    return `${colors.yellow}${status}${colors.reset}`
  }
  return `${colors.green}${status}${colors.reset}`
}

/**
 * Color duration based on time (green < 100ms, yellow < 500ms, red >= 500ms)
 */
function colorDuration(ms: number): string {
  if (ms >= 500) {
    return `${colors.red}${ms}ms${colors.reset}`
  }
  if (ms >= 100) {
    return `${colors.yellow}${ms}ms${colors.reset}`
  }
  return `${colors.green}${ms}ms${colors.reset}`
}

/**
 * Color cache status
 */
function colorCache(status: string | undefined): string {
  if (!status) {
    return ''
  }
  // no-store/skip = yellow (not cached), HIT = green, MISS = cyan
  if (status === 'HIT') {
    return ` ${colors.green}(${status})${colors.reset}`
  }
  if (status === 'MISS' || status === 'STALE') {
    return ` ${colors.cyan}(${status})${colors.reset}`
  }
  return ` ${colors.yellow}(${status})${colors.reset}`
}

/**
 * Format URL for display - extract hostname and truncate path
 */
function formatUrl(
  url: string,
  maxPathLength = 50
): { host: string; path: string } {
  try {
    const parsed = new URL(url)
    const host = parsed.hostname.replace(/^(www\.|api\.|apicdn\.)/, '')
    let path =
      parsed.pathname +
      (parsed.search ? '?' + parsed.search.slice(1, 30) + '...' : '')
    if (path.length > maxPathLength) {
      path = path.slice(0, maxPathLength) + '...'
    }
    return { host, path }
  } catch {
    // Not a valid URL, return as-is
    return { host: '', path: url.slice(0, maxPathLength) }
  }
}

/**
 * Parse LOG_FETCH_URLS env var into include/exclude patterns.
 * Format: "pattern1,pattern2,-excludePattern"
 * Prefix with - to exclude.
 *
 * @example
 * LOG_FETCH_URLS=sanity,commerce     // only log URLs containing these
 * LOG_FETCH_URLS=-nextjs,-telemetry  // log all except these
 * LOG_FETCH_URLS=sanity,-staging     // log sanity URLs except staging
 */
function parseUrlFilters(): { include: string[]; exclude: string[] } | null {
  const raw = process.env.LOG_FETCH_URLS
  if (!raw) {
    return null
  }

  const include: string[] = []
  const exclude: string[] = []

  const parts = raw
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
  for (const part of parts) {
    if (part.startsWith('-')) {
      exclude.push(part.slice(1).toLowerCase())
    } else {
      include.push(part.toLowerCase())
    }
  }

  return { include, exclude }
}

/**
 * Check if URL should be logged based on LOG_FETCH_URLS filter.
 */
function shouldLogUrl(
  url: string,
  filters: { include: string[]; exclude: string[] } | null
): boolean {
  if (!filters) {
    return true
  }

  const lowerUrl = url.toLowerCase()

  // Check excludes first
  for (const pattern of filters.exclude) {
    if (lowerUrl.includes(pattern)) {
      return false
    }
  }

  // If no includes specified, log everything (except excluded)
  if (filters.include.length === 0) {
    return true
  }

  // Check includes
  for (const pattern of filters.include) {
    if (lowerUrl.includes(pattern)) {
      return true
    }
  }

  return false
}

/**
 * Install custom fetch logging that uses Winston namespaced logger.
 * Patches globalThis.fetch to log all requests.
 * Automatically detects calling package from stack trace.
 *
 * Must be called from instrumentation.ts register() function.
 */
export function installFetchLogging(): void {
  const enabled =
    process.env.ENABLE_FETCH_LOGGING === 'true' ||
    process.env.ENABLE_FETCH_LOGGING === '1'

  if (!enabled) {
    return
  }

  // Use require() to prevent bundling issues
  // This file is only used in Node.js context from instrumentation.ts
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  const { createLogger } = require('@maestro/core/logger')

  // Helper to get or create a cached logger
  function getLogger(namespace: string): LoggerType {
    const cached = loggerCache.get(namespace)
    if (cached) {
      return cached
    }
    const logger = createLogger(namespace) as LoggerType
    loggerCache.set(namespace, logger)
    return logger
  }

  const originalFetch = globalThis.fetch

  // Parse URL filters once at startup
  const urlFilters = parseUrlFilters()

  globalThis.fetch = async function patchedFetch(
    input: RequestInfo | URL,
    init?: RequestInit
  ): Promise<Response> {
    // Extract URL string; mask before shortening so truncation cannot hide personal data
    const url = sanitizeString(
      typeof input === 'string'
        ? input
        : input instanceof URL
          ? input.href
          : input.url
    )

    // Check URL filter before doing anything else
    if (!shouldLogUrl(url, urlFilters)) {
      return originalFetch(input, init)
    }

    const method = init?.method || 'GET'
    const startTime = performance.now()
    const { host, path } = formatUrl(url)
    const jsonMode = isJsonLoggingEnabled()

    try {
      const response = await originalFetch(input, init)
      const duration = Math.round(performance.now() - startTime)

      // Check for cache status from various headers
      const cacheStatus =
        response.headers.get('x-nextjs-cache') ||
        response.headers.get('x-vercel-cache') ||
        (response.headers.get('cache-control')?.includes('no-store')
          ? 'no-store'
          : undefined)

      if (jsonMode) {
        logFetchJson({
          level: 'http',
          method,
          host,
          path,
          url,
          status: response.status,
          duration,
          cache: cacheStatus,
        })
      } else {
        const log = getLogger('maestro:fetch')
        // Format: METHOD host path STATUS DURATIONms (cache) - with colors
        const hostStr = host ? `${colors.cyan}${host}${colors.reset} ` : ''
        const statusStr = colorStatus(response.status)
        const durationStr = colorDuration(duration)
        const cacheStr = colorCache(cacheStatus)
        log.http(
          `${method} ${hostStr}${colors.dim}${path}${colors.reset} ${statusStr} ${durationStr}${cacheStr}`
        )
      }

      return response
    } catch (error) {
      const duration = Math.round(performance.now() - startTime)

      if (jsonMode) {
        logFetchJson({
          level: 'error',
          method,
          host,
          path,
          url,
          duration,
          error: error instanceof Error ? error.message : String(error),
        })
      } else {
        const log = getLogger('maestro:fetch')
        const hostStr = host ? `${colors.cyan}${host}${colors.reset} ` : ''
        const durationStr = colorDuration(duration)
        log.error(
          `${method} ${hostStr}${colors.dim}${path}${colors.reset} ${colors.red}FAILED${colors.reset} ${durationStr}`,
          {
            error: error instanceof Error ? error.message : String(error),
          }
        )
      }
      throw error
    }
  }

  const log = getLogger('maestro:fetch')
  log.debug('Custom fetch logging installed')
}
